import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  RefreshCw,
  Search,
  Calendar,
  FileText,
  Upload,
  Trash2,
  Eye,
  MoreVertical,
  Receipt,
  ChevronDown,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  getExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  getExpenseStatistics,
  getPcmAccounts,
  seedPcmAccounts,
  uploadExpenseDocument,
  formatCurrency,
  getExpenseSupplierName,
  PlatformExpense,
  CreateExpenseDto,
  ExpenseStatistics,
  PcmAccount,
  ExpenseDocumentType,
} from '@/services/expenseService';
import {
  getSuppliers,
  createSupplier,
  Supplier,
  CreateSupplierDto,
  SupplierCategory,
} from '@/services/supplierService';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';

// Simplified document types shown in the UI: Facture, Bon, Reçu
type SimpleDocumentType = 'INVOICE' | 'BON' | 'RECEIPT';
const SIMPLE_DOC_TYPES: { value: SimpleDocumentType; label: string }[] = [
  { value: 'INVOICE', label: 'Facture' },
  { value: 'BON', label: 'Bon' },
  { value: 'RECEIPT', label: 'Reçu' },
];

// Map any backend value (including legacy ones) to our 3-option UI value
const toSimpleDocType = (value?: ExpenseDocumentType | null): SimpleDocumentType => {
  if (value === 'RECEIPT') return 'RECEIPT';
  if (value === 'BON' || value === 'BON_COMMANDE' || value === 'BON_LIVRAISON') return 'BON';
  return 'INVOICE';
};

// "Bon" and "Reçu" are TVA-exonerated by default
const isTvaExemptDocType = (value: SimpleDocumentType): boolean =>
  value === 'BON' || value === 'RECEIPT';

const getSimpleDocLabel = (value: SimpleDocumentType): string =>
  SIMPLE_DOC_TYPES.find((t) => t.value === value)?.label ?? value;

export const ExpensesPage: React.FC = () => {
  const { toasts, removeToast, success, error } = useToast();
  const [expenses, setExpenses] = useState<PlatformExpense[]>([]);
  const [statistics, setStatistics] = useState<ExpenseStatistics | null>(null);
  const [pcmAccounts, setPcmAccounts] = useState<PcmAccount[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [newSupplier, setNewSupplier] = useState<CreateSupplierDto>({ name: '' });

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteMotif, setDeleteMotif] = useState('');
  const [selectedExpense, setSelectedExpense] = useState<PlatformExpense | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [creating, setCreating] = useState(false);

  // Dropdown menu
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Form state
  const initialForm: CreateExpenseDto & { documentType: SimpleDocumentType } = {
    compte: '',
    compteLibelle: '',
    description: '',
    amountHt: 0,
    tvaRate: 20,
    tvaExempt: false,
    expenseDate: new Date().toISOString().split('T')[0],
    documentType: 'INVOICE',
  };
  const [formData, setFormData] = useState(initialForm);
  const [documentFile, setDocumentFile] = useState<File | null>(null);

  const toastRef = useRef({ success, error });
  toastRef.current = { success, error };

  const monthOptions = React.useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthNames = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
      ];
      options.push({ value, label: `${monthNames[date.getMonth()]} ${date.getFullYear()}` });
    }
    return options;
  }, []);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const [year, month] = selectedMonth.split('-').map(Number);

      const [expensesData, statsData, accountsData, suppliersData] = await Promise.all([
        getExpenses({ periodYear: year, periodMonth: month }),
        getExpenseStatistics(year, month),
        getPcmAccounts(),
        getSuppliers({ isActive: true, archived: false }),
      ]);

      setExpenses(expensesData);
      setStatistics(statsData);
      setPcmAccounts(accountsData);
      setSuppliers(suppliersData);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
      toastRef.current.error('Erreur', 'Impossible de charger les charges');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (pcmAccounts.length === 0 && !loading) {
      seedPcmAccounts().then(() => {
        getPcmAccounts().then(setPcmAccounts);
      });
    }
  }, [pcmAccounts.length, loading]);

  const filteredExpenses = expenses.filter((exp) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const supplierName = exp.supplier?.name || exp.supplierName || '';
    return (
      exp.description.toLowerCase().includes(query) ||
      supplierName.toLowerCase().includes(query) ||
      exp.compte.includes(query) ||
      (exp.reference?.toLowerCase().includes(query) ?? false)
    );
  });

  const handleOpenMenu = (id: string) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      setMenuPosition(null);
      return;
    }
    const button = menuButtonRefs.current[id];
    if (button) {
      const rect = button.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: Math.max(8, rect.right - 192) });
    }
    setOpenMenuId(id);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const resetForm = () => {
    setFormData(initialForm);
    setDocumentFile(null);
  };

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await createExpense({
        ...formData,
        // Backend accepts our simplified types directly (BON is a real enum value)
        documentType: formData.documentType as ExpenseDocumentType,
      });

      if (documentFile) {
        try {
          await uploadExpenseDocument(created.id, documentFile);
        } catch (uploadErr: any) {
          toastRef.current.error(
            'Charge créée, mais upload échoué',
            uploadErr.message || 'Impossible d\'envoyer le document',
          );
        }
      }

      toastRef.current.success('Charge créée', 'La charge a été enregistrée');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      toastRef.current.error('Erreur', err.message || 'Impossible de créer la charge');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedExpense) return;
    try {
      await updateExpense(selectedExpense.id, {
        ...formData,
        documentType: formData.documentType as ExpenseDocumentType,
      });

      if (documentFile) {
        try {
          await uploadExpenseDocument(selectedExpense.id, documentFile);
        } catch (uploadErr: any) {
          toastRef.current.error(
            'Charge modifiée, mais upload échoué',
            uploadErr.message || 'Impossible d\'envoyer le document',
          );
        }
      }

      toastRef.current.success('Charge modifiée', 'La charge a été mise à jour');
      setShowDetailModal(false);
      setEditMode(false);
      setDocumentFile(null);
      fetchData();
    } catch (err: any) {
      toastRef.current.error('Erreur', err.message || 'Impossible de modifier la charge');
    }
  };

  const openDeleteModal = (id: string) => {
    setDeleteTargetId(id);
    setDeleteMotif('');
    setShowDeleteModal(true);
    closeMenu();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    if (!deleteMotif.trim()) {
      toastRef.current.error('Motif requis', 'Veuillez indiquer le motif de suppression');
      return;
    }
    try {
      await deleteExpense(deleteTargetId, deleteMotif.trim());
      toastRef.current.success('Charge supprimée', 'La charge a été supprimée');
      setShowDeleteModal(false);
      setDeleteTargetId(null);
      setDeleteMotif('');
      fetchData();
    } catch (err: any) {
      toastRef.current.error('Erreur', err.message || 'Impossible de supprimer la charge');
    }
  };

  const handleFileUpload = async (id: string, file: File) => {
    try {
      await uploadExpenseDocument(id, file);
      toastRef.current.success('Document envoyé', 'Le document justificatif a été enregistré');
      fetchData();
    } catch (err: any) {
      toastRef.current.error('Erreur', err.message || 'Impossible d\'envoyer le document');
    }
  };

  const openEditModal = (expense: PlatformExpense) => {
    const docType = toSimpleDocType(expense.documentType);
    setSelectedExpense(expense);
    setFormData({
      compte: expense.compte,
      compteLibelle: expense.compteLibelle,
      description: expense.description,
      reference: expense.reference,
      supplierId: expense.supplierId,
      supplierName: expense.supplierName || expense.supplier?.name,
      supplierIce: expense.supplierIce || expense.supplier?.ice,
      supplierIf: expense.supplierIf || expense.supplier?.ifiscal,
      amountHt: Number(expense.amountHt),
      tvaRate: Number(expense.tvaRate),
      tvaExempt: expense.tvaExempt,
      expenseDate: new Date(expense.expenseDate).toISOString().split('T')[0],
      documentType: docType,
      paymentMethod: expense.paymentMethod,
      paymentReference: expense.paymentReference,
      isPaid: expense.isPaid,
      notes: expense.notes,
    });
    setDocumentFile(null);
    setEditMode(true);
    setShowDetailModal(true);
    closeMenu();
  };

  const handleDocTypeChange = (next: SimpleDocumentType) => {
    setFormData((prev) => {
      if (isTvaExemptDocType(next)) {
        return { ...prev, documentType: next, tvaExempt: true, tvaRate: 0 };
      }
      // Switching back to Facture: re-enable TVA at the PCM account's default rate
      const accountRate = pcmAccounts.find((a) => a.compte === prev.compte)?.defaultTvaRate;
      const restoredRate = accountRate != null ? Number(accountRate) : 20;
      return {
        ...prev,
        documentType: next,
        tvaExempt: false,
        tvaRate: restoredRate || 20,
      };
    });
  };

  const calculatedTva = formData.tvaExempt ? 0 : (formData.amountHt * (formData.tvaRate || 20)) / 100;
  const calculatedTtc = formData.amountHt + calculatedTva;

  if (loading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Charges</h1>
            <p className="text-sm text-gray-500 mt-1">Plan Comptable Marocain - Classe 6</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            </div>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              <Plus size={16} />
              Nouvelle charge
            </button>
          </div>
        </div>

        {/* Statistics Cards (simplified: no en attente / validées split) */}
        {statistics && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Receipt className="text-indigo-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Charges du mois</p>
                  <p className="text-xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
              <p className="text-sm text-indigo-100">Montant HT</p>
              <p className="text-xl font-bold">{formatCurrency(statistics.validatedAmountHt)}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-4 text-white">
              <p className="text-sm text-purple-100">TVA déductible</p>
              <p className="text-xl font-bold">{formatCurrency(statistics.validatedTva)}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher par description, fournisseur, compte..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Expenses Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Compte</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Fournisseur</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">HT</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">TVA</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">TTC</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Doc</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredExpenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      {new Date(expense.expenseDate).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-gray-600">{expense.compte}</span>
                      <p className="text-xs text-gray-400">{expense.compteLibelle}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                      {expense.reference && (
                        <p className="text-xs text-gray-400">Réf: {expense.reference}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {getExpenseSupplierName(expense)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {getSimpleDocLabel(toSimpleDocType(expense.documentType))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">
                      {formatCurrency(expense.amountHt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-500">
                      {expense.tvaExempt ? 'Exonéré' : formatCurrency(expense.tvaAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">
                      {formatCurrency(expense.amountTtc)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {expense.documentUrl ? (
                        <a
                          href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${expense.documentUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <FileText size={18} />
                        </a>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        ref={(el) => { menuButtonRefs.current[expense.id] = el; }}
                        onClick={() => handleOpenMenu(expense.id)}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        <MoreVertical size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredExpenses.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                      Aucune charge trouvée pour cette période
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {(showCreateModal || (showDetailModal && editMode)) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {showCreateModal ? 'Nouvelle charge' : 'Modifier la charge'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Plan Comptable Marocain - Classe 6</p>
            </div>
            <div className="p-6 space-y-4">
              {/* PCM Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Compte comptable *
                </label>
                <select
                  value={formData.compte}
                  onChange={(e) => {
                    const account = pcmAccounts.find((a) => a.compte === e.target.value);
                    setFormData({
                      ...formData,
                      compte: e.target.value,
                      compteLibelle: account?.libelle || '',
                      tvaRate: account?.defaultTvaRate ?? 20,
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Sélectionner un compte</option>
                  {pcmAccounts.map((account) => (
                    <option key={account.id} value={account.compte}>
                      {account.compte} - {account.libelle}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Description de la charge"
                  required
                />
              </div>

              {/* Date + Reference */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.expenseDate}
                    onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    N° Facture fournisseur
                  </label>
                  <input
                    type="text"
                    value={formData.reference || ''}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ex: FAC-2026-001"
                  />
                </div>
              </div>

              {/* Supplier */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Fournisseur</h3>
                <select
                  value={formData.supplierId || ''}
                  onChange={(e) => {
                    const suppId = e.target.value;
                    if (suppId === '__NEW__') {
                      setShowAddSupplierModal(true);
                    } else if (suppId) {
                      const supp = suppliers.find((s) => s.id === suppId);
                      setFormData({
                        ...formData,
                        supplierId: suppId,
                        supplierName: supp?.name,
                        supplierIce: supp?.ice,
                        supplierIf: supp?.ifiscal,
                      });
                    } else {
                      setFormData({
                        ...formData,
                        supplierId: undefined,
                        supplierName: undefined,
                        supplierIce: undefined,
                        supplierIf: undefined,
                      });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- Sélectionner ou ajouter --</option>
                  <option value="__NEW__" className="font-semibold text-indigo-600">
                    + Ajouter un nouveau fournisseur
                  </option>
                  {suppliers.length > 0 && <option disabled>──────────────</option>}
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name} {supplier.ice ? `(ICE: ${supplier.ice})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Document Type + TVA logic */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Type de document</h3>
                <div className="grid grid-cols-3 gap-2">
                  {SIMPLE_DOC_TYPES.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleDocTypeChange(opt.value)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                        formData.documentType === opt.value
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {isTvaExemptDocType(formData.documentType as SimpleDocumentType) && (
                  <p className="mt-2 text-xs text-gray-500">
                    TVA exonérée automatiquement pour les bons et reçus.
                  </p>
                )}
              </div>

              {/* Amounts */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Montants</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Montant HT *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amountHt}
                      onChange={(e) => setFormData({ ...formData, amountHt: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taux TVA (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.tvaRate}
                      onChange={(e) => setFormData({ ...formData, tvaRate: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                      disabled={formData.tvaExempt}
                    />
                    {formData.tvaExempt && (
                      <p className="mt-1 text-xs text-gray-500">Exonéré</p>
                    )}
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3">
                    <p className="text-xs text-indigo-600 mb-1">Total TTC</p>
                    <p className="text-xl font-bold text-indigo-700">{formatCurrency(calculatedTtc)}</p>
                    <p className="text-xs text-indigo-500">TVA: {formatCurrency(calculatedTva)}</p>
                  </div>
                </div>
              </div>

              {/* Document Upload */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Justificatif (PDF, image)
                </h3>
                {selectedExpense?.documentUrl && !documentFile && (
                  <div className="mb-2 p-2 bg-gray-50 rounded-lg flex items-center justify-between text-sm">
                    <a
                      href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${selectedExpense.documentUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                    >
                      <FileText size={14} /> Document actuel
                    </a>
                    <span className="text-xs text-gray-400">
                      Sélectionner un nouveau fichier pour remplacer
                    </span>
                  </div>
                )}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.xlsx,.xls"
                  onChange={(e) => setDocumentFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {documentFile && (
                  <p className="mt-1 text-xs text-gray-500">{documentFile.name}</p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Notes optionnelles"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowDetailModal(false);
                  setEditMode(false);
                  resetForm();
                  setSelectedExpense(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={showCreateModal ? handleCreate : handleUpdate}
                disabled={creating || !formData.compte || !formData.description || !formData.amountHt}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {showCreateModal ? (creating ? 'Création…' : 'Créer la charge') : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Read-only Detail Modal */}
      {showDetailModal && !editMode && selectedExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Détails de la charge</h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedExpense.compte} - {selectedExpense.compteLibelle}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">
                    {new Date(selectedExpense.expenseDate).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Référence</p>
                  <p className="font-medium">{selectedExpense.reference || '-'}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="font-medium">{selectedExpense.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Fournisseur</p>
                  <p className="font-medium">{getExpenseSupplierName(selectedExpense)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type de document</p>
                  <p className="font-medium">
                    {getSimpleDocLabel(toSimpleDocType(selectedExpense.documentType))}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <p className="text-sm text-gray-500">Montant HT</p>
                  <p className="text-lg font-bold">{formatCurrency(selectedExpense.amountHt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    TVA {selectedExpense.tvaExempt ? '(exonérée)' : `(${selectedExpense.tvaRate}%)`}
                  </p>
                  <p className="text-lg font-bold">{formatCurrency(selectedExpense.tvaAmount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total TTC</p>
                  <p className="text-lg font-bold text-indigo-600">
                    {formatCurrency(selectedExpense.amountTtc)}
                  </p>
                </div>
              </div>
              {selectedExpense.documentUrl && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Document justificatif</p>
                  <a
                    href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${selectedExpense.documentUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                  >
                    <FileText size={16} />
                    Voir le document
                  </a>
                </div>
              )}
              {selectedExpense.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-gray-700">{selectedExpense.notes}</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowDetailModal(false); setSelectedExpense(null); }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Fermer
              </button>
              <button
                onClick={() => openEditModal(selectedExpense)}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete with motif */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Supprimer la charge</h2>
              <p className="text-sm text-gray-500 mt-1">
                Pour des raisons comptables, un motif est obligatoire.
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motif de suppression *
              </label>
              <textarea
                value={deleteMotif}
                onChange={(e) => setDeleteMotif(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Ex: Doublon, charge enregistrée par erreur..."
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteTargetId(null); setDeleteMotif(''); }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={!deleteMotif.trim()}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dropdown Menu */}
      {openMenuId && menuPosition && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={closeMenu} />
          <div
            className="fixed w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <button
              onClick={() => {
                const expense = expenses.find((e) => e.id === openMenuId);
                if (expense) {
                  setSelectedExpense(expense);
                  setEditMode(false);
                  setShowDetailModal(true);
                }
                closeMenu();
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Eye size={14} className="text-gray-400" /> Voir détails
            </button>
            <button
              onClick={() => openEditModal(expenses.find((e) => e.id === openMenuId)!)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <FileText size={14} className="text-gray-400" /> Modifier
            </button>
            <label className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer">
              <Upload size={14} className="text-gray-400" />
              {expenses.find((e) => e.id === openMenuId)?.documentUrl
                ? 'Remplacer le document'
                : 'Ajouter un document'}
              <input
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && openMenuId) handleFileUpload(openMenuId, file);
                  closeMenu();
                }}
              />
            </label>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={() => openMenuId && openDeleteModal(openMenuId)}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 size={14} /> Supprimer
            </button>
          </div>
        </>,
        document.body,
      )}

      {/* Add New Supplier Modal */}
      {showAddSupplierModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouveau fournisseur</h2>
              <p className="text-sm text-gray-500 mt-1">
                Ajouter un nouveau fournisseur à la liste
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom / Raison sociale *
                </label>
                <input
                  type="text"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: LYDEC, Maroc Telecom..."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ICE (15 chiffres)
                  </label>
                  <input
                    type="text"
                    value={newSupplier.ice || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, ice: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
                    placeholder="000000000000000"
                    maxLength={15}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IF (Identifiant Fiscal)
                  </label>
                  <input
                    type="text"
                    value={newSupplier.ifiscal || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, ifiscal: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select
                    value={newSupplier.category || 'OTHER'}
                    onChange={(e) => setNewSupplier({ ...newSupplier, category: e.target.value as SupplierCategory })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="UTILITIES">Utilities (Eau, Électricité, Télécom)</option>
                    <option value="RENT">Loyers et charges locatives</option>
                    <option value="INSURANCE">Assurances</option>
                    <option value="MAINTENANCE">Entretien et réparations</option>
                    <option value="IT_SERVICES">Services informatiques</option>
                    <option value="PROFESSIONAL">Honoraires professionnels</option>
                    <option value="OFFICE_SUPPLIES">Fournitures de bureau</option>
                    <option value="EQUIPMENT">Équipements</option>
                    <option value="MARKETING">Publicité et communication</option>
                    <option value="TRANSPORT">Transport et déplacements</option>
                    <option value="BANKING">Services bancaires</option>
                    <option value="GOVERNMENT">Impôts et taxes</option>
                    <option value="OTHER">Autres</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input
                    type="text"
                    value={newSupplier.city || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Casablanca"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input
                  type="text"
                  value={newSupplier.address || ''}
                  onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={newSupplier.phone || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newSupplier.email || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowAddSupplierModal(false); setNewSupplier({ name: '' }); }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  if (!newSupplier.name.trim()) {
                    toastRef.current.error('Erreur', 'Le nom du fournisseur est requis');
                    return;
                  }
                  try {
                    const created = await createSupplier(newSupplier);
                    setSuppliers([...suppliers, created]);
                    setFormData({
                      ...formData,
                      supplierId: created.id,
                      supplierName: created.name,
                      supplierIce: created.ice,
                      supplierIf: created.ifiscal,
                    });
                    setShowAddSupplierModal(false);
                    setNewSupplier({ name: '' });
                    toastRef.current.success('Succès', 'Fournisseur ajouté avec succès');
                  } catch (err) {
                    console.error('Failed to create supplier:', err);
                    toastRef.current.error('Erreur', 'Impossible de créer le fournisseur');
                  }
                }}
                disabled={!newSupplier.name.trim()}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Ajouter le fournisseur
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpensesPage;
